/**
 * Shared argument handling for the Core_Formula_Set implementations.
 *
 * Every function in `functions/` reads its arguments through the helpers here,
 * so the coercion behaviour of Requirement 4.10 is decided in one place:
 *
 * - a *scalar* argument is coerced strictly (`SUM("3")` is 3, `SUM("x")` is
 *   `#VALUE!`),
 * - a *range or array* argument contributes only the cells of the wanted type
 *   (`SUM(A1:A3)` ignores text and blanks, exactly like Excel),
 * - an error anywhere in a consumed range propagates.
 *
 * Criteria parsing (`">5"`, `"<>x"`, `"a*"`) and the wildcard matcher are here
 * too, because `SUMIF`, `COUNTIF`, `AVERAGEIF`, their `-IFS` forms, and `SEARCH`
 * must agree on them.
 */

import type { ErrorCode } from '../../types.js';
import {
  BLANK,
  cellsOf,
  compareScalars,
  err,
  gridOf,
  isErrCode,
  isError,
  toBool,
  toNumber,
  toScalar,
  toText,
  type EvalContext,
  type Grid,
  type Scalar,
  type Value,
} from '../values.js';

/** An argument coercion that failed, carrying the error to return. */
export type Failure = { fail: Scalar };

export function failure(code: ErrorCode): Failure {
  return { fail: err(code) };
}

export function isFailure<T>(v: T | Failure): v is Failure {
  return typeof v === 'object' && v !== null && 'fail' in (v as Record<string, unknown>);
}

/** The i-th argument, or `undefined` when the caller omitted it. */
export function arg(args: readonly Value[], i: number): Value | undefined {
  return args[i];
}

/** The i-th argument collapsed to a scalar (a multi-cell operand is `#VALUE!`). */
export function scalarArg(args: readonly Value[], i: number, ctx: EvalContext): Scalar {
  const a = args[i];
  if (a === undefined) return BLANK;
  return toScalar(a, ctx);
}

/** The i-th argument as a number, or a {@link Failure}. */
export function numberArg(args: readonly Value[], i: number, ctx: EvalContext): number | Failure {
  const s = scalarArg(args, i, ctx);
  const n = toNumber(s);
  return isErrCode(n) ? failure(n) : n;
}

/** The i-th argument as a number, defaulting when it is absent. */
export function optNumberArg(
  args: readonly Value[],
  i: number,
  ctx: EvalContext,
  fallback: number,
): number | Failure {
  if (args[i] === undefined) return fallback;
  return numberArg(args, i, ctx);
}

/** The i-th argument as a truncated integer. */
export function intArg(args: readonly Value[], i: number, ctx: EvalContext): number | Failure {
  const n = numberArg(args, i, ctx);
  return isFailure(n) ? n : Math.trunc(n);
}

export function optIntArg(
  args: readonly Value[],
  i: number,
  ctx: EvalContext,
  fallback: number,
): number | Failure {
  if (args[i] === undefined) return fallback;
  return intArg(args, i, ctx);
}

/** The i-th argument as text. */
export function textArg(args: readonly Value[], i: number, ctx: EvalContext): string | Failure {
  const s = scalarArg(args, i, ctx);
  const t = toText(s);
  return isErrCode(t) ? failure(t) : t;
}

export function optTextArg(
  args: readonly Value[],
  i: number,
  ctx: EvalContext,
  fallback: string,
): string | Failure {
  if (args[i] === undefined) return fallback;
  return textArg(args, i, ctx);
}

/** The i-th argument as a boolean. */
export function boolArg(args: readonly Value[], i: number, ctx: EvalContext): boolean | Failure {
  const s = scalarArg(args, i, ctx);
  const b = toBool(s);
  return isErrCode(b) ? failure(b) : b;
}

export function optBoolArg(
  args: readonly Value[],
  i: number,
  ctx: EvalContext,
  fallback: boolean,
): boolean | Failure {
  if (args[i] === undefined) return fallback;
  return boolArg(args, i, ctx);
}

/** True when the argument is a range or an array rather than a single value. */
export function isGridArg(v: Value | undefined): boolean {
  return v !== undefined && (v.k === 'ref' || v.k === 'array');
}

/**
 * Numbers contributed by one argument, per the rule at the top of this module.
 * `blanksAsZero` is used by `SUMPRODUCT`, which treats every non-numeric cell as
 * zero instead of skipping it.
 */
function numbersFrom(
  value: Value,
  ctx: EvalContext,
  out: number[],
  blanksAsZero: boolean,
): Failure | null {
  if (value.k === 'ref' || value.k === 'array') {
    for (const cell of cellsOf(value, ctx)) {
      if (isError(cell)) return { fail: cell };
      if (cell.k === 'number') out.push(cell.v);
      else if (blanksAsZero) out.push(0);
    }
    return null;
  }
  if (isError(value)) return { fail: value };
  if (value.k === 'empty') {
    if (blanksAsZero) out.push(0);
    return null;
  }
  const n = toNumber(value);
  if (isErrCode(n)) return failure(n);
  out.push(n);
  return null;
}

/** Every number contributed by `args`, or the first error encountered. */
export function collectNumbers(
  args: readonly Value[],
  ctx: EvalContext,
  opts: { blanksAsZero?: boolean; from?: number } = {},
): number[] | Failure {
  const out: number[] = [];
  for (let i = opts.from ?? 0; i < args.length; i++) {
    const value = args[i];
    if (value === undefined) continue;
    const bad = numbersFrom(value, ctx, out, opts.blanksAsZero ?? false);
    if (bad !== null) return bad;
  }
  return out;
}

/** Every scalar contributed by `args`, ranges expanded, errors preserved. */
export function collectScalars(args: readonly Value[], ctx: EvalContext, from = 0): Scalar[] {
  const out: Scalar[] = [];
  for (let i = from; i < args.length; i++) {
    const value = args[i];
    if (value === undefined) continue;
    if (value.k === 'ref' || value.k === 'array') out.push(...cellsOf(value, ctx));
    else out.push(value);
  }
  return out;
}

/** First error among `args` (searching inside ranges), or `null`. */
export function firstError(args: readonly Value[], ctx: EvalContext): Scalar | null {
  for (const cell of collectScalars(args, ctx)) {
    if (isError(cell)) return cell;
  }
  return null;
}

/* ---------------------------------------------------------------- criteria */

/** Comparison operator a criteria string may carry. */
export type CriteriaOp = '=' | '<>' | '>' | '>=' | '<' | '<=';

/** A parsed `SUMIF`-style criterion. */
export interface Criteria {
  op: CriteriaOp;
  /** The comparand, already typed (number, boolean, or text). */
  operand: Scalar;
  /** Set when the operand is a text pattern containing `*` or `?`. */
  pattern: string | null;
}

const OPERATORS: readonly CriteriaOp[] = ['<>', '<=', '>=', '=', '<', '>'];

/** Parses a criterion. A bare value means equality; `"*"`/`"?"` enable wildcards. */
export function parseCriteria(raw: Scalar, ctx: EvalContext): Criteria {
  if (raw.k !== 'text') {
    return { op: '=', operand: raw, pattern: null };
  }
  const source = raw.v;
  let op: CriteriaOp = '=';
  let rest = source;
  for (const candidate of OPERATORS) {
    if (source.startsWith(candidate)) {
      op = candidate;
      rest = source.slice(candidate.length);
      break;
    }
  }
  const trimmed = rest.trim();
  const asNumber = trimmed.length === 0 ? '#VALUE!' : toNumber({ k: 'text', v: trimmed });
  if (!isErrCode(asNumber)) {
    return { op, operand: { k: 'number', v: asNumber }, pattern: null };
  }
  const folded = trimmed.toUpperCase();
  if (folded === 'TRUE' || folded === 'FALSE') {
    return { op, operand: { k: 'bool', v: folded === 'TRUE' }, pattern: null };
  }
  void ctx;
  const hasWildcard = (op === '=' || op === '<>') && /[*?]/.test(trimmed);
  return {
    op,
    operand: trimmed.length === 0 ? BLANK : { k: 'text', v: trimmed },
    pattern: hasWildcard ? trimmed : null,
  };
}

/** Applies a parsed criterion to one cell. Errors never match. */
export function matchesCriteria(cell: Scalar, criteria: Criteria): boolean {
  if (isError(cell)) return false;
  if (criteria.pattern !== null) {
    const t = cell.k === 'text' ? cell.v : cell.k === 'empty' ? '' : (toText(cell) as string);
    const hit = wildcardMatch(t, criteria.pattern);
    return criteria.op === '<>' ? !hit : hit;
  }
  if (criteria.operand.k === 'empty') {
    const blank = cell.k === 'empty';
    return criteria.op === '<>' ? !blank : blank;
  }
  // A blank cell only ever matches an explicit blank criterion or `<>`.
  if (cell.k === 'empty') return criteria.op === '<>';
  const cmp = compareScalars(cell, criteria.operand);
  switch (criteria.op) {
    case '=':
      return cmp === 0;
    case '<>':
      return cmp !== 0;
    case '>':
      return cmp > 0;
    case '>=':
      return cmp >= 0;
    case '<':
      return cmp < 0;
    case '<=':
      return cmp <= 0;
  }
}

/**
 * Excel wildcard matching: `*` matches any run, `?` matches one character, `~`
 * escapes either. Implemented as a two-pointer scan rather than a generated
 * regular expression, so a user pattern can never become executable input.
 * Comparison is case-insensitive.
 */
export function wildcardMatch(subject: string, pattern: string): boolean {
  const s = subject.toUpperCase();
  const p = pattern.toUpperCase();
  let si = 0;
  let pi = 0;
  let star = -1;
  let match = 0;
  while (si < s.length) {
    const pc = p[pi];
    if (pc === '~' && pi + 1 < p.length) {
      if (s[si] === p[pi + 1]) {
        si += 1;
        pi += 2;
        continue;
      }
    } else if (pc !== undefined && (pc === '?' || pc === s[si])) {
      si += 1;
      pi += 1;
      continue;
    } else if (pc === '*') {
      star = pi;
      match = si;
      pi += 1;
      continue;
    }
    if (star >= 0) {
      pi = star + 1;
      match += 1;
      si = match;
      continue;
    }
    return false;
  }
  while (p[pi] === '*') pi += 1;
  return pi === p.length;
}

/* ------------------------------------------------- conditional aggregation */

/** One `criteria_range, criteria` pair of a `-IFS` call. */
export interface CriteriaPair {
  grid: Grid;
  criteria: Criteria;
}

/**
 * Reads `range, criteria` pairs starting at `from`. Every criteria range must
 * have the same shape, which is what Excel requires and what makes the row-wise
 * conjunction well defined.
 */
export function readCriteriaPairs(
  args: readonly Value[],
  from: number,
  ctx: EvalContext,
): CriteriaPair[] | Failure {
  if ((args.length - from) % 2 !== 0) return failure('#VALUE!');
  const pairs: CriteriaPair[] = [];
  for (let i = from; i < args.length; i += 2) {
    const rangeArg = args[i];
    const criteriaArg = args[i + 1];
    if (rangeArg === undefined || criteriaArg === undefined) return failure('#VALUE!');
    const grid = gridOf(rangeArg, ctx);
    const criteriaScalar = toScalar(criteriaArg, ctx);
    if (isError(criteriaScalar)) return { fail: criteriaScalar };
    pairs.push({ grid, criteria: parseCriteria(criteriaScalar, ctx) });
  }
  if (pairs.length === 0) return failure('#VALUE!');
  const first = pairs[0] as CriteriaPair;
  for (const pair of pairs) {
    if (pair.grid.rows !== first.grid.rows || pair.grid.cols !== first.grid.cols) {
      return failure('#VALUE!');
    }
  }
  return pairs;
}

/** Row/column offsets of the cells satisfying every criterion. */
export function matchingCells(pairs: readonly CriteriaPair[]): { row: number; col: number }[] {
  const first = pairs[0];
  if (first === undefined) return [];
  const hits: { row: number; col: number }[] = [];
  for (let r = 0; r < first.grid.rows; r++) {
    for (let c = 0; c < first.grid.cols; c++) {
      let all = true;
      for (const pair of pairs) {
        if (!matchesCriteria(pair.grid.get(r, c), pair.criteria)) {
          all = false;
          break;
        }
      }
      if (all) hits.push({ row: r, col: c });
    }
  }
  return hits;
}

/**
 * The grid values are summed over. When `sumArg` is absent the criteria range
 * itself is used, and when it is present it is aligned to the criteria range's
 * top-left corner, which is how Excel resolves a mis-sized `sum_range`.
 */
export function alignedSumGrid(
  sumArg: Value | undefined,
  criteriaGrid: Grid,
  ctx: EvalContext,
): Grid {
  if (sumArg === undefined) return criteriaGrid;
  const source = gridOf(sumArg, ctx);
  return {
    rows: criteriaGrid.rows,
    cols: criteriaGrid.cols,
    get: (row, col) => (row < source.rows && col < source.cols ? source.get(row, col) : BLANK),
  };
}
